"use client";

/**
 * FormFieldRenderer.tsx (Dashboard)
 *
 * REFACTORED to use UnifiedFormField architecture
 *
 * This component now acts as a thin wrapper that:
 * 1. Handles dashboard-specific features (signature panel, image viewer modal)
 * 2. Delegates field rendering to UnifiedFormField
 *
 * Reduced from 712 lines to ~120 lines
 */

import React, { useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { API_BASE_URL } from "../environments/environment";
import { SignatureFieldInternal } from "./signaturePanel";
import { dataURLtoFile } from "./utils";
import { CapturedImage } from "../types/types";
import { UnifiedFormField } from "./form-fields";

interface FormFieldRendererProps {
  field: any;
  value: any;
  errorClass: string;
  errors: string[];
  handleChange: (fieldId: string, value: any) => void;
  openCameraModal: (field: any, promptId?: string) => void;
  openPaymentModal?: () => void;
  formValues?: Record<string, any>;
  isPaymentForm?: boolean;
  paymentStatus?: 'processing' | 'cancelled' | 'failed';
  /** Server-resolved payment amount, major units. See BaseFieldProps. */
  preloadedAmount?: number;
}

const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  field,
  value,
  errorClass,
  errors,
  handleChange,
  openCameraModal,
  openPaymentModal,
  formValues,
  isPaymentForm = false,
  paymentStatus,
  preloadedAmount,
}) => {
  const [selectedImageForView, setSelectedImageForView] = useState<string | null>(null);

  // Determine if field has error based on errorClass
  const hasError = errorClass.includes('border-red-500');

  // Special case: signature-pad uses custom SignatureFieldInternal component
  if (field.type === 'signature-pad') {
    return (
      <>
        {field.description && (
          <span className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
            {field.description}
          </span>
        )}
        <div className="flex flex-col gap-2">
          <SignatureFieldInternal
            field={field}
            value={value}
            onChange={handleChange}
            errorClass={errorClass}
            dataURLtoFile={dataURLtoFile}
            baseClasses="w-full px-4 py-2 border rounded-md text-sm bg-white text-black"
          />
          {errors.map((error: string, index: number) => (
            <p key={index} className="text-xs text-red-500 mt-1">
              {error}
            </p>
          ))}
        </div>
      </>
    );
  }

  // Special case: multi_image_capture has image viewer modal
  if (field.type === 'multi_image_capture') {
    // Get images array
    const images = Array.isArray(value) ? value : [];

    // Determine if using new format (with prompts) or legacy format
    const hasPrompts = field.prompts && field.prompts.length > 0;
    const isNewFormat = images.length > 0 &&
      typeof images[0] === "object" && images[0] !== null && "promptId" in images[0];

    return (
      <>
        {/* Render the field using UnifiedFormField */}
        <UnifiedFormField
          fieldConfig={field}
          mode="dashboard"
          value={value}
          onChange={handleChange}
          errors={errors}
          hasError={hasError}
          openCameraModal={openCameraModal}
          openPaymentModal={openPaymentModal}
          formValues={formValues}
          isPaymentForm={isPaymentForm}
          paymentStatus={paymentStatus}
          preloadedAmount={preloadedAmount}
        />

        {/* Image Viewer Modal - dashboard-specific feature */}
        {selectedImageForView && (
          <Dialog open={!!selectedImageForView} onOpenChange={() => setSelectedImageForView(null)}>
            <DialogContent className="max-w-[95vw] sm:max-w-2xl lg:max-w-4xl">
              <DialogHeader>
                <DialogTitle>Image Preview</DialogTitle>
              </DialogHeader>
              <div className="flex items-center justify-center p-4">
                <img
                  src={selectedImageForView}
                  alt="Full size preview"
                  className="max-w-full max-h-[80vh] object-contain"
                />
              </div>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }

  // Default: Use UnifiedFormField for all other field types
  return (
    <UnifiedFormField
      fieldConfig={field}
      mode="dashboard"
      value={value}
      onChange={handleChange}
      errors={errors}
      hasError={hasError}
      openCameraModal={openCameraModal}
      openPaymentModal={openPaymentModal}
      formValues={formValues}
      isPaymentForm={isPaymentForm}
      paymentStatus={paymentStatus}
      preloadedAmount={preloadedAmount}
    />
  );
};

export default React.memo(FormFieldRenderer);
